/**
 * URL utility functions for handling image URLs and API endpoints
 */

/**
 * Detect the current environment and return appropriate configuration
 * @returns {Object} Environment configuration
 */
const getEnvironmentConfig = () => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Check if we're running in Docker (production-like environment)
  const isDockerized = process.env.REACT_APP_BACKEND_URL && 
                      process.env.REACT_APP_BACKEND_URL.includes('/api');
  
  return {
    isDevelopment,
    isLocalhost,
    isDockerized,
    currentOrigin: window.location.origin
  };
};

/**
 * Get the appropriate backend URL based on environment
 * @returns {string} - Backend base URL
 */
export const getBackendUrl = () => {
  const config = getEnvironmentConfig();
  
  // Use environment variable if set
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Fallback logic based on environment detection
  if (config.isDevelopment && config.isLocalhost) {
    // Development environment - backend typically runs on port 8000
    return 'http://localhost:8000';
  }
  
  // Production fallback - assume API is proxied
  return `${config.currentOrigin}/api`;
};

/**
 * Convert relative URLs to absolute backend URLs with environment-aware resolution
 * @param {string} url - The URL to convert
 * @returns {string} - Absolute URL pointing to the backend server
 */
export const getAbsoluteImageUrl = (url) => {
  if (!url) return url;
  
  // If already absolute URL, return as-is (but check for localhost:3000 conflicts)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Fix localhost:3000 conflicts by redirecting to proper backend
    if (url.includes('localhost:3000')) {
      console.warn('Detected localhost:3000 in image URL, redirecting to backend:', url);
      const backendUrl = getBackendUrl();
      // Extract the path part after localhost:3000
      const pathMatch = url.match(/localhost:3000(.*)$/);
      if (pathMatch) {
        const cleanUrl = pathMatch[1].startsWith('/') ? pathMatch[1].substring(1) : pathMatch[1];
        return `${backendUrl}/${cleanUrl}`;
      }
    }
    return url;
  }
  
  // Handle data URLs and blob URLs
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  
  // Convert relative URLs to absolute backend URLs
  const backendUrl = getBackendUrl();
  
  // Special handling for uploads paths - ensure they have /api prefix
  if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
    // Convert /uploads/ to /api/uploads/ for consistency
    const modifiedUrl = url.replace('/uploads/', '/api/uploads/');
    // Remove leading slash if present to avoid double slashes
    const cleanUrl = modifiedUrl.startsWith('/') ? modifiedUrl.substring(1) : modifiedUrl;
    
    // Ensure we don't create malformed URLs
    if (!cleanUrl) {
      return backendUrl;
    }
    
    return `${backendUrl}/${cleanUrl}`;
  }
  
  // Standard handling for other URLs
  // Remove leading slash if present to avoid double slashes
  const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
  
  // Ensure we don't create malformed URLs
  if (!cleanUrl) {
    return backendUrl;
  }
  
  return `${backendUrl}/${cleanUrl}`;
};

/**
 * Convert an array of image URLs to absolute URLs
 * @param {Array} urls - Array of URLs to convert
 * @returns {Array} - Array of absolute URLs
 */
export const getAbsoluteImageUrls = (urls) => {
  if (!Array.isArray(urls)) return [];
  return urls.map(url => getAbsoluteImageUrl(url)).filter(Boolean);
};

/**
 * Check if a URL is already absolute
 * @param {string} url - URL to check
 * @returns {boolean} - True if URL is absolute
 */
export const isAbsoluteUrl = (url) => {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:');
};

/**
 * Generate multiple fallback URLs for failed image loads
 * @param {string} originalUrl - The original URL that failed
 * @returns {Array<string>} - Array of fallback URLs to try
 */
export const generateFallbackUrls = (originalUrl) => {
  if (!originalUrl || isAbsoluteUrl(originalUrl)) {
    return [];
  }
  
  const fallbackUrls = [];
  const backendUrl = getBackendUrl();
  const config = getEnvironmentConfig();
  
  // Clean the URL path
  const cleanUrl = originalUrl.startsWith('/') ? originalUrl.substring(1) : originalUrl;
  
  // Primary fallback: current backend URL
  fallbackUrls.push(`${backendUrl}/${cleanUrl}`);
  
  // Secondary fallback: try different backend ports in development
  if (config.isDevelopment && config.isLocalhost) {
    const commonPorts = ['8000', '8001'];
    commonPorts.forEach(port => {
      const fallbackBackend = `http://localhost:${port}`;
      if (fallbackBackend !== backendUrl) {
        fallbackUrls.push(`${fallbackBackend}/${cleanUrl}`);
      }
    });
  }
  
  // Tertiary fallback: try with /api prefix if not already present
  if (!backendUrl.includes('/api')) {
    fallbackUrls.push(`${config.currentOrigin}/api/${cleanUrl}`);
  }
  
  // Remove duplicates
  return [...new Set(fallbackUrls)];
};

/**
 * Add cache-busting parameter to URL for retry attempts
 * @param {string} url - URL to add cache buster to
 * @param {boolean} force - Force add cache buster even if URL already has parameters
 * @returns {string} - URL with cache-busting parameter
 */
export const addCacheBuster = (url, force = false) => {
  if (!url) return url;
  
  // Don't add cache buster to data URLs or blob URLs
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  
  // Check if URL already has cache buster (unless forced)
  if (!force && url.includes('_cb=')) {
    return url;
  }
  
  const separator = url.includes('?') ? '&' : '?';
  const timestamp = Date.now();
  return `${url}${separator}_cb=${timestamp}`;
};

/**
 * Smart URL resolution with automatic fallback generation
 * @param {string} url - Original URL
 * @param {Object} options - Resolution options
 * @param {boolean} options.generateFallbacks - Whether to generate fallback URLs
 * @param {boolean} options.addCacheBuster - Whether to add cache-busting parameter
 * @returns {Object} - Object with primary URL and fallbacks
 */
export const resolveImageUrl = (url, options = {}) => {
  const { generateFallbacks: shouldGenerateFallbacks = false, addCacheBuster: shouldAddCacheBuster = false } = options;
  
  if (!url) {
    return { primary: url, fallbacks: [] };
  }
  
  // Get the primary resolved URL
  let primary = getAbsoluteImageUrl(url);
  
  // Add cache buster if requested
  if (shouldAddCacheBuster) {
    primary = addCacheBuster(primary);
  }
  
  // Generate fallbacks if requested
  const fallbacks = shouldGenerateFallbacks ? generateFallbackUrls(url) : [];
  
  // Add cache buster to fallbacks if requested
  const processedFallbacks = shouldAddCacheBuster 
    ? fallbacks.map(fallbackUrl => addCacheBuster(fallbackUrl))
    : fallbacks;
  
  return {
    primary,
    fallbacks: processedFallbacks.filter(fallbackUrl => fallbackUrl !== primary)
  };
};